/**
 * Approved exceptions the CFM GO import records on a migrated policy, and the Coverage C minimum a
 * migrated renewal is raised to. #37093 (product decisions on #37040)
 *
 * Both approvals follow #37002's wind/hail approval: the approval is the APPROVED VALUE (plus
 * reason / by / at), not a flag, and it holds only while the stored value still equals it. Once staff
 * change the value, the ordinary rule applies again with nothing to clear. Renewals clone the
 * coverages, so an approval carries onto every later term. The importer is the only writer; the
 * policy-mod and rater-v2 guards (`enforceMigratedLimitApprovals` in @ourbranch/rater-helpers) keep
 * them server-owned.
 *
 *   rcWithoutRceOverride  FO-6 structure. GO settled it at Replacement Cost but exports no RCE for
 *                         structures, so staff-fe's "RC loss settlement selected but RCE data not
 *                         available" rule blocked every endorsement. The approved value is Coverage E.
 *   poolLimitOverride     FO-1–FO-4 above-ground pool limit GO wrote below EmberTech's $2,500 base.
 */

/** The above-ground pool limit included with FO-1–FO-4 dwellings; staff-fe rejects a lower total. */
export const ABOVE_GROUND_POOL_INCLUDED_LIMIT = 2500;

/** Holds the structure's RC-without-RCE approval (FO-6 structure coverages). */
export interface RcWithoutRceApprovalHolder {
  coverageE?: number | null;
  structureLossSettlement?: string | null;
  roofLossSettlement?: string | null;
  rcWithoutRceOverride?: number | null;
}

/** Holds a dwelling's pool limit approval (FO-1–FO-4 coverages). */
export interface PoolLimitApprovalHolder {
  poolLimit?: number | null;
  poolLimitOverride?: number | null;
}

const sameAmount = (stored: number | null | undefined, approved: number | null | undefined): boolean =>
  approved != null && stored != null && Number(stored) === Number(approved);

/**
 * Whether staff-fe's RC rule applies to this structure: RC structure settlement or an RC roof. Mirrors
 * `rc80PercentRequirement` in staff-fe yup-helpers.
 */
export function triggersRcWithoutRceCheck(
  coverages: Pick<RcWithoutRceApprovalHolder, 'structureLossSettlement' | 'roofLossSettlement'> | null | undefined
): boolean {
  return coverages?.structureLossSettlement === 'RC' || coverages?.roofLossSettlement === 'RC';
}

/**
 * True while a structure's RC-without-RCE approval holds: Coverage E is still the approved amount,
 * the structure is still RC-settled, and it still has no RCE (once one exists the RC rule can be
 * checked normally).
 */
export function isRcWithoutRceApproved({
  coverages,
  building
}: {
  coverages: RcWithoutRceApprovalHolder | null | undefined;
  building: { replacementCostEstimate?: { total?: number | null } | null } | null | undefined;
}): boolean {
  return (
    sameAmount(coverages?.coverageE, coverages?.rcWithoutRceOverride) &&
    triggersRcWithoutRceCheck(coverages) &&
    !(Number(building?.replacementCostEstimate?.total) > 0)
  );
}

/** True while the stored pool limit is the approved one (a $0 limit can be approved). */
export function isPoolLimitApproved(coverages: PoolLimitApprovalHolder | null | undefined): boolean {
  return sameAmount(coverages?.poolLimit, coverages?.poolLimitOverride);
}

/**
 * The smallest whole-dollar Coverage C the percent-of-Coverage-A rule (`coverageC >= coverageA × pct`,
 * unrounded) accepts. Every computed Coverage C minimum uses it: the post-data rules' clamp, the renewal
 * carry-over (#37093), and the staff-fe floor and default (#37109), so none lands cents under the rule.
 */
export function wholeDollarCoverageCMinimum(coverageA: number, minPctOfCoverageA: number): number {
  return Math.ceil(coverageA * minPctOfCoverageA);
}

/**
 * A Coverage C this far or further under the percent-of-Coverage-A minimum is a deliberate lower ratio,
 * not rounding: GO stored whole dollars, so rounding leaves a migrated Coverage C at most 99 cents short
 * of the unrounded `A × pct`. #37109
 */
export const COVERAGE_C_ROUNDING_SHORTFALL_LIMIT = 1;

/**
 * True when Coverage C is short of the percent-of-Coverage-A minimum (`A × pct`, unrounded) by
 * `COVERAGE_C_ROUNDING_SHORTFALL_LIMIT` ($1) or more: a genuinely lower ratio, such as a deliberate
 * 50%-of-A Coverage C on an FO-3. A migrated renewal keeps that ratio (#34560), and it stays
 * grandfathered on the renewal term while its inputs are unchanged. A shortfall under $1 is rounding,
 * and the renewal is raised to `wholeDollarCoverageCMinimum`. #37109
 */
export function isGenuinelyBelowCoverageCMinimum({
  coverageA,
  coverageC,
  minPctOfCoverageA
}: {
  coverageA: number | null | undefined;
  coverageC: number | null | undefined;
  minPctOfCoverageA: number | null | undefined;
}): boolean {
  if (coverageA == null || coverageC == null || minPctOfCoverageA == null) {
    return false;
  }
  return Number(coverageA) * minPctOfCoverageA - Number(coverageC) >= COVERAGE_C_ROUNDING_SHORTFALL_LIMIT;
}

/**
 * The largest whole-dollar Coverage C that is still genuinely below the percent-of-Coverage-A minimum
 * (`isGenuinelyBelowCoverageCMinimum`): `floor(A × pct − $1)`. A renewal that keeps a grandfathered
 * ratio caps its rounded Coverage C here, so rounding never moves it into the sub-dollar band that the
 * renewal term's endorsements and staff-fe treat as rounding to be raised (adversarial review on #37111:
 * A $204,029 renewed a $1-short ratio to $153,021, $0.75 under $153,021.75). #37109
 */
export function largestGrandfatheredCoverageC(coverageA: number, minPctOfCoverageA: number): number {
  return Math.floor(coverageA * minPctOfCoverageA - COVERAGE_C_ROUNDING_SHORTFALL_LIMIT);
}
